/** Small colour helpers shared by the agency branding and the site appearance. */

const TONE_BACKGROUNDS = {
  dark: '#0b0f14',
  light: '#fff7ed',
  slate: '#f1f5f9',
} as const;

/** WCAG contrast of a hex colour against a storefront tone. True when body text would be hard to read. */
export function primaryFailsContrast(primary: string, tone: keyof typeof TONE_BACKGROUNDS): boolean {
  const ratio = contrastRatio(primary, TONE_BACKGROUNDS[tone]);
  return ratio !== null && ratio < 4.5;
}

function contrastRatio(foreground: string, background: string): number | null {
  const first = luminance(foreground);
  const second = luminance(background);
  if (first === null || second === null) return null;
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

function luminance(hex: string): number | null {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  const channel = (offset: number): number => {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** Scale an #RRGGBB colour toward black; returns the input when unparseable. */
export function darkenHex(hex: string, factor = 0.9): string {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  const scale = (offset: number): string => {
    const value = Math.round(Number.parseInt(hex.slice(offset, offset + 2), 16) * factor);
    return Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0');
  };
  return `#${scale(1)}${scale(3)}${scale(5)}`;
}
